import {
  UndeliverCouponsResponse,
  undeliverCoupons,
  unholdCoupons,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import {
  DELIVERY_CHANNEL_CONFIG,
  DeliveryChannel,
  GiftCouponForRedemption,
} from '@shared/api/giftCoupon/giftCoupon.interface';

/**
 * 발송완료 취소 실행 — 사유 입력·확인 UI는 UndeliverModal이 담당하고,
 * 여기는 호출 + 실패 안내(404 = 구서버)만 맡는다. 실패 시 null.
 *
 * `hold: true`면 발송대기가 아니라 **발송보류(OnHold)** 로 남긴다. 보류를 모르는
 * 구서버는 이 필드를 무시하고 발송대기로 복귀시키므로, 응답에 `held`가 없으면
 * 여기서 바로 경고한다(조용히 대기 큐에 섞이는 것이 제일 위험하다).
 * 스펙: docs/gift-coupon-undeliver-spec.md · docs/gift-coupon-onhold-spec.md
 */
export async function executeUndeliver(
  couponIds: string[],
  reason: string,
  hold?: boolean,
): Promise<UndeliverCouponsResponse | null> {
  try {
    const result = await undeliverCoupons(couponIds, reason, hold);
    if (hold && result.held === undefined) {
      alert(
        '발송보류는 서버 업데이트 후 사용 가능합니다.\n이번 건은 발송취소만 처리되어 발송대기 목록으로 돌아갔습니다.',
      );
    }
    return result;
  } catch (error) {
    const e = error as {
      response?: { status?: number; data?: { message?: string } };
    };
    if (e?.response?.status === 404) {
      alert('발송취소: 서버 업데이트 후 사용 가능');
    } else {
      // 정산서 포함·비Delivered 등 서버 전체검증 메시지를 그대로 노출
      alert(e?.response?.data?.message || '발송취소 실패');
    }
    return null;
  }
}

/** 완료 토스트 문구 — 키워드 한 줄 (예: "발송취소 3건 · 환급 +25,500원") */
export function formatUndeliverToast(result: UndeliverCouponsResponse): string {
  const restoredSum = result.restored.reduce((sum, r) => sum + r.amount, 0);
  const restoredText =
    restoredSum > 0 ? `환급 +${restoredSum.toLocaleString()}원` : '환급 없음';
  const heldText = result.held ? ` · 발송보류 ${result.held}건` : '';
  return `발송취소 ${result.undelivered}건${heldText} · ${restoredText}`;
}

/**
 * 발송보류 해제 실행 (OnHold → 발송대기). 실패 시 false — 404는 구서버 안내.
 * 스펙: docs/gift-coupon-onhold-spec.md
 */
export async function executeUnhold(
  couponIds: string[],
  reason?: string,
): Promise<number | null> {
  try {
    const result = await unholdCoupons(couponIds, reason);
    return result.unheld;
  } catch (error) {
    const e = error as {
      response?: { status?: number; data?: { message?: string } };
    };
    if (e?.response?.status === 404) {
      alert('발송보류 해제: 서버 업데이트 후 사용 가능');
    } else {
      alert(e?.response?.data?.message || '발송보류 해제 실패');
    }
    return null;
  }
}

/** 누적 발송취소 횟수 — 보류 권고(5회 이상) 판단 기준 */
export function undeliverCountOf(coupon: GiftCouponForRedemption): number {
  return coupon.undeliverLogs?.length ?? 0;
}

/**
 * 반복 취소 경고선 — 이번 취소를 포함해 5회 이상이면 발송보류를 기본값으로 권한다.
 * (같은 건을 계속 재발송하다 실패하는 쿠폰은 대기 큐에 섞여 있으면 안 된다)
 */
export const HOLD_SUGGEST_UNDELIVER_COUNT = 5;

export function shouldSuggestHold(coupons: GiftCouponForRedemption[]): boolean {
  return coupons.some(
    (c) => undeliverCountOf(c) + 1 >= HOLD_SUGGEST_UNDELIVER_COUNT,
  );
}

export interface UndeliverChannelEstimate {
  /** null = 매체 미입력 (환급 없음) — 원장 잔액 대조도 불가 */
  channel: DeliveryChannel | null;
  label: string;
  count: number;
  sum: number;
  isUnknown: boolean;
}

/** 모달 요약용 채널별 환급 예상 — 실제 환급은 서버가 원장 기장분에 한해 수행 */
export function estimateUndeliverByChannel(
  coupons: GiftCouponForRedemption[],
): UndeliverChannelEstimate[] {
  const byChannel = new Map<string, UndeliverChannelEstimate>();
  for (const c of coupons) {
    const ch = c.gifticon?.deliveryChannel;
    const label = ch ? DELIVERY_CHANNEL_CONFIG[ch]?.label ?? ch : '미지정';
    const entry = byChannel.get(label) ?? {
      channel: ch ?? null,
      label,
      count: 0,
      sum: 0,
      isUnknown: !ch,
    };
    entry.count++;
    entry.sum += c.gifticon?.priceWhole ?? 0;
    byChannel.set(label, entry);
  }
  // 미지정(환급 불가)은 맨 뒤로
  return Array.from(byChannel.values()).sort(
    (a, b) => Number(a.isUnknown) - Number(b.isUnknown),
  );
}
